/**
 * Skin Analysis (P1) — HTTP wire contracts. Response types alias the
 * domain entities in model/skinAnalysis/skinAnalysis.ts.
 *
 * Source of truth: "Skin Analysis P1 API Specification" (2026-08-01), the same document the
 * model layer follows, checked against the DEPLOYED Facade backend (origin/dev) — its live
 * NestJS controllers and SkinAnalysisAppResult.ts, not the Notion copy, which has drifted.
 */

import type {
  SkinAnalysisContentType,
  SkinAnalysisEnvironment,
  SkinAnalysisHautRunStartResponse,
  SkinAnalysisImageRole,
  SkinAnalysisInFlightStatus,
  SkinAnalysisImageUploadTarget,
  SkinAnalysisLocation,
  SkinAnalysisLocationPreference,
  SkinAnalysisMyOverview,
  SkinAnalysisRecommendationProductDetail,
  SkinAnalysisRecommendationProducts,
  SkinAnalysisRecommendations,
  SkinAnalysisReport,
  SkinAnalysisSavedSurvey,
  SkinAnalysisResultFailure,
  SkinAnalysisResultRef,
  SkinAnalysisResultSections,
  SkinAnalysisResultStatus,
  SkinAnalysisStatusOverview,
  SkinAnalysisSurveyConfig,
  SkinAnalysisSurveyQuery,
  SkinAnalysisSurveyResponseRef,
  SkinAnalysisQualityIssue,
} from '../../../model/skinAnalysis/skinAnalysis';
import type { Currency } from '../../../constants/currency/currency';

// ----- API 1: GET /skinAnalysis/me/status -----

export type GetMySkinAnalysisStatusResponse = SkinAnalysisStatusOverview;

// ----- GET /skinAnalysis/me/overview — Skin Diary entry page (single call) -----

export type GetMySkinAnalysisOverviewResponse = SkinAnalysisMyOverview;

// ----- API 2: GET /skinAnalysis/survey/active -----

/**
 * `currency` renders the budget question's labels ("Under $30" / "Under S$40" / …).
 * Optional on the wire and defaults to USD server-side, so omitting it prices the budget
 * bands in dollars for every region.
 */
export type GetActiveSkinAnalysisSurveyRequest = SkinAnalysisSurveyQuery;

export type GetActiveSkinAnalysisSurveyResponse = SkinAnalysisSurveyConfig;

// ----- GET /skinAnalysis/me/survey -----

export type GetMySkinAnalysisSurveyResponse = SkinAnalysisSavedSurvey;

// ----- API 3: POST /skinAnalysis/survey/responses -----

export type SkinAnalysisSurveyAnswer = {
  questionId: string;
  /** Always a string array — single, multi, and ranked_multi. For ranked_multi, order is the ranking. */
  answer: string[];
};

export type SubmitSkinAnalysisSurveyResponseRequest = {
  /**
   * Echo of the version `GET /survey/active` returned. REQUIRED, and validated server-side
   * against the registered version list — an unknown string is a 400, not a fallback to
   * inference. Stored responses hold option ids only, so this is what lets the read-only
   * survey view rebuild the exact wording the customer answered.
   */
  surveyVersion: string;
  answers: SkinAnalysisSurveyAnswer[];
};

/** INFERRED — contract says the server generates `surveyResponseId`; response body shape assumed. */
export type SubmitSkinAnalysisSurveyResponseResponse = SkinAnalysisSurveyResponseRef;

// ----- API 4: GET/PUT /skinAnalysis/me/location -----

export type GetSkinAnalysisLocationResponse = SkinAnalysisLocationPreference;

/**
 * PUT body — backend `SetSkinAnalysisLocationRequest` accepts `locationId` ONLY
 * (@Matches /^[A-Z]{2}:[a-z0-9:-]+$/). Everything else (countryName, city, displayName)
 * is resolved server-side from the deterministic key.
 */
export type PutSkinAnalysisLocationRequest = {
  locationId: string;
};

// ----- GET /skinAnalysis/me/environment -----

export type GetSkinAnalysisEnvironmentResponse = SkinAnalysisEnvironment;

export type PutSkinAnalysisLocationResponse = SkinAnalysisLocationPreference;

// ----- GET /skinAnalysis/locations/search (city autocomplete, worldwide) -----

/** `query` required; `countryCode` optional filter (omit for a worldwide search). */
export type SearchSkinAnalysisLocationsRequest = {
  query: string;
  countryCode?: string;
};

/** Each result is a fully-resolved, city-level location (carries its own `locationId`). */
export type SearchSkinAnalysisLocationsResponse = {
  locations: SkinAnalysisLocation[];
};

// ----- API 5: POST /skinAnalysis/image-upload-url -----

export type CreateSkinAnalysisImageUploadUrlRequest = {
  role: SkinAnalysisImageRole;
  contentType: SkinAnalysisContentType;
  /** Image byte size — optional on the backend (@IsOptional @IsInt @IsPositive @Max 10485760);
   *  the app sends it when measurable so the server can pre-authorize the upload size. */
  contentLength?: number;
};

export type CreateSkinAnalysisImageUploadUrlResponse = SkinAnalysisImageUploadTarget;

// ----- POST /skinAnalysis/hautRuns (SSD-6097) -----

/** One uploaded photo — echo the objectKey returned by image-upload-url. */
export type SkinAnalysisSubmittedImage = {
  role: SkinAnalysisImageRole;
  objectKey: string;
};

/** Starts a survey-independent image run. Exactly front, left, and right. */
export type CreateSkinAnalysisHautRunRequest = {
  images: SkinAnalysisSubmittedImage[];
};

export type CreateSkinAnalysisHautRunResponse = SkinAnalysisHautRunStartResponse;

// ----- API 6: POST /skinAnalysis/results -----

/**
 * Original shape: the photos go straight in and the server pairs them with the caller's
 * latest saved survey response. Still accepted by the server; the app no longer sends it.
 */
export type CreateSkinAnalysisResultFromImagesRequest = {
  images: SkinAnalysisSubmittedImage[];
  hautRunId?: never;
  surveyResponseId?: never;
  /**
   * Storefront currency. Optional on the wire and defaults to USD server-side, which is
   * why omitting it priced every recommendation in dollars regardless of the user's region.
   */
  currency?: Currency;
};

/**
 * SSD-6097 shape: a Haut run started right after the scan, bound to the survey response the
 * user finished afterwards. `images` must be absent — the run already carries them.
 */
export type CreateSkinAnalysisResultFromHautRunRequest = {
  hautRunId: string;
  surveyResponseId: string;
  images?: never;
  currency?: Currency;
};

export type CreateSkinAnalysisResultRequest =
  | CreateSkinAnalysisResultFromImagesRequest
  | CreateSkinAnalysisResultFromHautRunRequest;

/**
 * A newly created result — or the one already running.
 *
 * `status` is NOT always `queued`: the server is idempotent for an equivalent resubmission
 * (identical object keys and currency) and answers with the EXISTING result, which by then may
 * already be `processing` or `vendor_polling`. A caller that assumed `queued` would advance a
 * fresh-analysis UI over an analysis that is halfway done.
 */
export type CreateSkinAnalysisResultResponse = {
  resultId: string;
  status: SkinAnalysisInFlightStatus;
};

/**
 * Synchronous quality-failure body of `POST /results` (contract API 6). The client
 * detects this and throws `SkinAnalysisImageQualityError` instead of returning a result.
 */
export type CreateSkinAnalysisResultQualityFailure = {
  code: 'image_quality_failed';
  message: string;
  qualityIssues: SkinAnalysisQualityIssue[];
};

// ----- API 7: GET /skinAnalysis/results/{resultId}/status -----

export type GetSkinAnalysisResultStatusPathParams = SkinAnalysisResultRef;

export type GetSkinAnalysisResultStatusResponse = {
  resultId: string;
  status: SkinAnalysisResultStatus;
  /**
   * Per-section readiness (additive, Facade PR #1026): the report publishes before the
   * recommendations. Optional so a backend that predates the progressive contract
   * still parses — readers go through the model's isSkinAnalysis*Ready helpers.
   */
  sections?: SkinAnalysisResultSections;
  createdAt: string;
  updatedAt: string;
  /** Absent/null until the result reaches a terminal status. */
  completedAt?: string | null;
  /** Present on a failed/retake result — `retakeRequired` routes to retake-all-three. */
  failure?: SkinAnalysisResultFailure | null;
};

// ----- API 8: GET /skinAnalysis/results/{resultId}/report -----

export type GetSkinAnalysisReportPathParams = SkinAnalysisResultRef;

export type GetSkinAnalysisReportResponse = SkinAnalysisReport;

// ----- API 9: GET /skinAnalysis/results/{resultId}/recommendations -----

export type GetSkinAnalysisRecommendationsPathParams = SkinAnalysisResultRef;

export type GetSkinAnalysisRecommendationsResponse = SkinAnalysisRecommendations;

// ----- GET /skinAnalysis/results/{resultId}/recommendationProducts — live product list (SSD-6340) -----

/**
 * `currency` is what the products are re-priced in on every read. Optional on the wire (USD by
 * default) but the app always sends the storefront currency — and keys the query on it, since two
 * regions are two different price lists.
 */
export type GetSkinAnalysisRecommendationProductsRequest = SkinAnalysisResultRef & {
  currency?: Currency;
};

export type GetSkinAnalysisRecommendationProductsResponse = SkinAnalysisRecommendationProducts;

// ----- GET /skinAnalysis/results/{resultId}/recommendationProducts/{recommendationItemId} -----

/** The detail is addressed by the occurrence's own UUID from the list, never by `productId`. */
export type GetSkinAnalysisRecommendationProductDetailRequest = SkinAnalysisResultRef & {
  recommendationItemId: string;
  currency?: Currency;
};

export type GetSkinAnalysisRecommendationProductDetailResponse = SkinAnalysisRecommendationProductDetail;
